import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { Box, Button, IconButton, Drawer, List, ListItemButton, ListItemText } from '@mui/material';
import { MenuRounded, CloseRounded } from '@mui/icons-material';
import { getToken } from '../auth';
import { BrandLogo } from './brand';
import SiteFooter from './SiteFooter';
import ChatButton from './ChatButton';

export const BUILDER_URL = 'https://builder.anainfo.ai';

const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Templates', to: '/templates' },
  { label: 'Features', to: '/#features' },
  { label: 'Pricing', to: '/#pricing' },
];

export { BrandLogo };

// Account button: signed-in admins go straight to their dashboard.
const AccountButton = ({ fullWidth }) => {
  const authed = !!getToken();
  return (
    <Button component={RouterLink} to={authed ? '/dashboard' : '/login'} variant="contained" fullWidth={fullWidth} sx={{ px: 3 }}>
      {authed ? 'Dashboard' : 'Login'}
    </Button>
  );
};

const Header = ({ dark }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  // Transparent over the dark hero, solid white once the page scrolls.
  const light = dark && !scrolled;

  return (
    <Box component="header" sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100,
      transition: 'background-color .25s ease, box-shadow .25s ease',
      bgcolor: light ? 'transparent' : 'rgba(255,255,255,0.92)',
      backdropFilter: light ? 'none' : 'saturate(180%) blur(12px)',
      boxShadow: light ? 'none' : '0 1px 0 rgba(27,29,43,0.06)',
    }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, md: 4 }, height: 72, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}><BrandLogo light={light} /></Box>
        <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          {NAV.map(n => (
            <Button key={n.label} component={RouterLink} to={n.to}
              sx={{ color: light ? 'rgba(255,255,255,0.85)' : 'text.primary', fontWeight: 500, '&:hover': { color: light ? '#fff' : 'primary.main', bgcolor: 'transparent' } }}>
              {n.label}
            </Button>
          ))}
          <Box sx={{ ml: 1.5 }}><AccountButton /></Box>
        </Box>
        <IconButton aria-label="Open menu" onClick={() => setOpen(true)} sx={{ display: { md: 'none' }, color: light ? '#fff' : 'text.primary' }}>
          <MenuRounded />
        </IconButton>
      </Box>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} slotProps={{ paper: { sx: { width: 280, p: 2 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <BrandLogo size={32} />
          <IconButton aria-label="Close menu" onClick={() => setOpen(false)}><CloseRounded /></IconButton>
        </Box>
        <List>
          {NAV.map(n => (
            <ListItemButton key={n.label} component={RouterLink} to={n.to} sx={{ borderRadius: 2 }}>
              <ListItemText primary={n.label} />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ mt: 2 }}><AccountButton fullWidth /></Box>
      </Drawer>
    </Box>
  );
};

// Public marketing pages share this header and footer. On the home page the
// header starts transparent over the dark hero.
const SiteLayout = () => {
  const { pathname, hash } = useLocation();

  // Scroll to "#section" links, or to the top on a normal page change.
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header dark={pathname === '/'} />
      <Box component="main" sx={{ flex: 1 }}><Outlet /></Box>
      <SiteFooter />
      <ChatButton />
    </Box>
  );
};

export default SiteLayout;
